import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function SafetyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const safetySchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '安心・安全への取り組み | おかやま健康製薬株式会社',
    description: 'おかやま健康製薬の品質管理体制、製造工程、安全基準をご紹介。ISO認証取得工場での製造、厳選された原材料、徹底した品質検査により、安心・安全な製品をお届けします。',
    url: `${siteUrl}/safety`,
    inLanguage: 'ja-JP',
    isPartOf: {
      '@type': 'WebSite',
      name: 'おかやま健康製薬株式会社',
      url: siteUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '安心・安全への取り組み',
          item: `${siteUrl}/safety`,
        },
      ],
    },
  };

  const qualityPoints = [
    {
      id: 1,
      title: '厳選された原材料',
      description:
        '国内外から厳選した高品質な原材料のみを使用。すべての原材料は産地・品質・安全性を徹底的に確認し、トレーサビリティを確保しています。',
      image:
        'https://public.readdy.ai/ai/img_res/4c7f8aa5cbe023c67f4da7de4d62c7d8.jpg',
    },
    {
      id: 2,
      title: 'クリーンな製造環境',
      description:
        'ISO認証を取得した最新設備の製造工場で生産。徹底した衛生管理のもと、クリーンルームでの製造を行い、異物混入を防止しています。',
      image:
        'https://readdy.ai/api/search-image?query=modern%20clean%20pharmaceutical%20manufacturing%20facility%20with%20stainless%20steel%20equipment%20and%20bright%20white%20sterile%20environment%20japanese%20workers%20in%20protective%20suits%20quality%20control%20high%20tech%20factory%20photography&width=500&height=500&seq=quality2&orientation=squarish',
    },
    {
      id: 3,
      title: '厳格な品質検査',
      description:
        '製造工程の各段階で品質検査を実施。最終製品は第三者機関による検査も受け、安全性と品質を二重にチェックしています。',
      image:
        'https://readdy.ai/api/search-image?query=japanese%20laboratory%20technician%20in%20white%20coat%20carefully%20examining%20samples%20with%20microscope%20in%20modern%20bright%20laboratory%20professional%20quality%20control%20testing%20scientific%20photography%20high%20resolution&width=500&height=500&seq=quality3&orientation=squarish',
    },
    {
      id: 4,
      title: '適切な保管・配送',
      description:
        '温度・湿度管理された倉庫で保管し、品質を維持したまま配送。お客様のお手元に届くまで、製品の品質を守り続けます。',
      image:
        'https://readdy.ai/api/search-image?query=organized%20modern%20warehouse%20with%20temperature%20controlled%20storage%20shelves%20filled%20with%20packaged%20supplements%20clean%20bright%20environment%20logistics%20management%20professional%20photography%20high%20quality&width=500&height=500&seq=quality4&orientation=squarish',
    },
  ];

  const manufacturingSteps = [
    {
      id: 1,
      icon: 'ri-search-line',
      title: '研究開発',
      description: '最新の栄養学研究に基づき、効果的な配合を設計',
    },
    {
      id: 2,
      icon: 'ri-flask-line',
      title: '原材料選定',
      description: '厳選された高品質な原材料を調達',
    },
    {
      id: 3,
      icon: 'ri-settings-3-line',
      title: '製造',
      description: 'ISO認証工場でクリーンな環境下で製造',
    },
    {
      id: 4,
      icon: 'ri-microscope-line',
      title: '品質検査',
      description: '厳格な品質基準に基づく多段階検査',
    },
    {
      id: 5,
      icon: 'ri-truck-line',
      title: '出荷',
      description: '適切な保管・配送でお客様のもとへ',
    },
  ];

  const certifications = [
    {
      id: 1,
      name: 'ISO 9001認証',
      description: '品質マネジメントシステム',
    },
    {
      id: 2,
      name: 'GMP認証',
      description: '適正製造規範',
    },
    {
      id: 3,
      name: 'HACCP認証',
      description: '衛生管理システム',
    },
    {
      id: 4,
      name: '健康食品GMP',
      description: '健康食品製造基準',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="安心・安全への取り組み | 品質管理・製造工程 | おかやま健康製薬株式会社"
        description="おかやま健康製薬の品質管理体制、製造工程、安全基準をご紹介。ISO認証取得工場での製造、厳選された原材料、徹底した品質検査により、安心・安全な製品をお届けします。岡山から全国へ、信頼の品質をお約束します。"
        keywords="岡山,品質管理,安全基準,ISO認証,製造工程,おかやま健康製薬"
        canonical={`${siteUrl}/safety`}
        schema={safetySchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[40vh] mt-16 lg:mt-20 bg-gradient-to-br from-[#4A90E2] to-[#2A6D8D] flex items-center justify-center">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=abstract%20soft%20flowing%20waves%20pattern%20in%20gentle%20blue%20and%20white%20colors%20representing%20health%20and%20wellness%20clean%20minimalist%20design%20smooth%20gradients%20peaceful%20atmosphere%20high%20quality&width=1920&height=800&seq=productshero&orientation=landscape')] bg-cover bg-center opacity-10"></div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            安心・安全への取り組み
          </h1>
        </div>
      </section>

      {/* Quality Points Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">
              品質へのこだわり
            </h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto mb-6"></div>
            <p className="text-sm lg:text-base text-gray-600 max-w-[800px] mx-auto leading-relaxed">
              お客様に安心してご利用いただけるよう、原材料の選定から製造、配送まで、すべての工程で徹底した品質管理を行っています。
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {qualityPoints.map((point) => (
              <article
                key={point.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6 lg:p-8">
                  <div className="w-full h-48 sm:h-full">
                    <img
                      src={point.image}
                      alt={point.title}
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D] mb-4">
                      {point.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{point.description}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Flow Section */}
      <section className="py-12 lg:py-20 bg-[#F5FAFE]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">
              開発製造の流れ
            </h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto mb-6"></div>
            <p className="text-sm lg:text-base text-gray-600 max-w-[800px] mx-auto leading-relaxed">
              研究開発から出荷まで、すべての工程で品質と安全性を追求。科学的根拠に基づいた製品づくりの流れをご紹介します。
            </p>
          </div>

          {/* ステップ概要 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6 mb-12 lg:mb-20">
            {manufacturingSteps.map((step, index) => (
              <article
                key={step.id}
                className="relative bg-white rounded-2xl p-5 lg:p-6 shadow-lg text-center hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#4A90E2] text-white rounded-full text-2xl mx-auto mb-3">
                  <i className={step.icon}></i>
                </div>
                <div className="absolute -top-3 -right-3 w-7 h-7 flex items-center justify-center bg-[#1A4D6D] text-white rounded-full font-bold text-xs">
                  {index + 1}
                </div>
                <h3 className="text-base lg:text-lg font-bold text-[#1A4D6D] mb-2">{step.title}</h3>
                <p className="text-xs lg:text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </article>
            ))}
          </div>

          {/* 詳細セクション1: 研究開発 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-16">
            <div className="w-full h-64 sm:h-72 lg:h-80 rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=japanese%20researchers%20in%20white%20lab%20coats%20discussing%20supplement%20formulation%20data%20on%20computer%20screens%20in%20modern%20bright%20pharmaceutical%20research%20laboratory%20with%20scientific%20equipment%20clean%20environment%20professional%20photography%20no%20text%20no%20signs&width=700&height=500&seq=mfg_research1&orientation=landscape"
                alt="研究開発の様子"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#4A90E2] text-white rounded-full font-bold text-sm">1</div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D]">研究開発</h3>
              </div>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                最新の栄養学研究や臨床データを分析し、お客様の健康課題に最適な成分配合を設計します。社内の研究チームが国内外の学術論文を精査し、エビデンスに基づいた処方を開発しています。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                各成分の相互作用や吸収効率まで考慮した独自の配合技術により、効果を最大限に引き出す製品設計を実現しています。
              </p>
            </div>
          </div>

          {/* 詳細セクション2: 原材料選定 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-16">
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#4A90E2] text-white rounded-full font-bold text-sm">2</div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D]">原材料選定</h3>
              </div>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                国内外の信頼できるサプライヤーから、品質基準を満たした原材料のみを厳選して調達します。すべての原材料は産地証明書を取得し、トレーサビリティを確保しています。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                残留農薬検査、重金属検査、微生物検査など、複数の安全性試験をクリアした原材料のみを使用。品質に妥協することなく、安心・安全な素材選びを徹底しています。
              </p>
            </div>
            <div className="order-1 lg:order-2 w-full h-64 sm:h-72 lg:h-80 rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=high%20quality%20natural%20raw%20ingredients%20for%20supplements%20including%20dried%20herbs%20vitamins%20minerals%20capsules%20arranged%20neatly%20on%20clean%20white%20surface%20bright%20natural%20lighting%20close%20up%20product%20photography%20no%20text%20no%20labels&width=700&height=500&seq=mfg_material1&orientation=landscape"
                alt="厳選された原材料"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* 詳細セクション3: 製造 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-16">
            <div className="w-full h-64 sm:h-72 lg:h-80 rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20sterile%20pharmaceutical%20manufacturing%20clean%20room%20with%20stainless%20steel%20machinery%20and%20conveyor%20belt%20producing%20supplement%20capsules%20japanese%20workers%20in%20protective%20clothing%20bright%20white%20environment%20professional%20industrial%20photography%20no%20text%20no%20signs&width=700&height=500&seq=mfg_factory1&orientation=landscape"
                alt="クリーンルームでの製造"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#4A90E2] text-white rounded-full font-bold text-sm">3</div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D]">製造</h3>
              </div>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                ISO認証を取得した最新設備の工場で、クリーンルーム環境のもと製造を行います。温度・湿度を厳密に管理し、異物混入を防止する多重チェック体制を整えています。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                製造工程の各段階で中間検査を実施し、規格に適合した製品のみを次工程へ送ります。熟練した技術者が設備の稼働状況を常時監視し、安定した品質を維持しています。
              </p>
            </div>
          </div>

          {/* 詳細セクション4: 品質検査 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-16">
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#4A90E2] text-white rounded-full font-bold text-sm">4</div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D]">品質検査</h3>
              </div>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                完成した製品は、社内の品質管理部門による厳格な検査を受けます。成分含有量の分析、溶出試験、安定性試験など、多角的な品質評価を実施しています。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                さらに、第三者検査機関による独立した品質検査も定期的に実施。ダブルチェック体制により、お客様に安心してご利用いただける品質を保証しています。
              </p>
            </div>
            <div className="order-1 lg:order-2 w-full h-64 sm:h-72 lg:h-80 rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=japanese%20female%20laboratory%20technician%20in%20white%20coat%20carefully%20analyzing%20supplement%20samples%20using%20advanced%20testing%20equipment%20in%20bright%20modern%20quality%20control%20laboratory%20professional%20scientific%20photography%20no%20text%20no%20signs&width=700&height=500&seq=mfg_inspect1&orientation=landscape"
                alt="品質検査の様子"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* 詳細セクション5: 出荷 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="w-full h-64 sm:h-72 lg:h-80 rounded-3xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=organized%20modern%20warehouse%20with%20neatly%20stacked%20supplement%20product%20boxes%20on%20shelves%20temperature%20controlled%20storage%20facility%20bright%20clean%20environment%20logistics%20and%20shipping%20preparation%20professional%20photography%20no%20text%20no%20signs&width=700&height=500&seq=mfg_ship1&orientation=landscape"
                alt="出荷準備の様子"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#4A90E2] text-white rounded-full font-bold text-sm">5</div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#1A4D6D]">出荷</h3>
              </div>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed mb-4">
                品質検査に合格した製品は、温度・湿度管理された倉庫で適切に保管されます。出荷前にも最終確認を行い、パッケージの状態や表示内容に問題がないことを確認します。
              </p>
              <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                お客様のお手元に届くまで品質を維持するため、配送方法にもこだわっています。迅速かつ丁寧な配送体制で、新鮮な状態の製品をお届けします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Guidelines Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">
              安心・安全のための指針
            </h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto"></div>
          </div>
          <div className="bg-gradient-to-br from-[#E8F4F8] to-[#D0E8F2] rounded-3xl p-6 lg:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#4A90E2] text-white rounded-xl flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1A4D6D] mb-2">
                      原材料の安全性確保
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      すべての原材料について、残留農薬検査、重金属検査、微生物検査を実施し、安全性を確認しています。
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#4A90E2] text-white rounded-xl flex-shrink-0">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1A4D6D] mb-2">
                      トレーサビリティの確保
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      原材料の調達から製造、出荷まで、すべての工程を記録・管理し、製品の追跡を可能にしています。
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#4A90E2] text-white rounded-xl flex-shrink-0">
                    <i className="ri-test-tube-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1A4D6D] mb-2">定期的な品質検査</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      製造ロットごとに品質検査を実施し、規格に適合した製品のみを出荷しています。
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#4A90E2] text-white rounded-xl flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1A4D6D] mb-2">
                      お客様サポート体制
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      製品に関するご質問やご相談に、専門スタッフが丁寧にお答えします。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-12 lg:py-20 bg-[#F5FAFE]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-4">認証・資格</h2>
            <div className="w-16 h-1 bg-[#4A90E2] mx-auto"></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert) => (
              <article
                key={cert.id}
                className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-lg transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#E8F4F8] rounded-full mx-auto mb-4">
                  <i className="ri-award-line text-3xl text-[#4A90E2]"></i>
                </div>
                <h3 className="text-base lg:text-lg font-bold text-[#1A4D6D] mb-2">
                  {cert.name}
                </h3>
                <p className="text-xs lg:text-sm text-gray-600">{cert.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}